import axios from 'axios';

const LOCAL_STORAGE_API = process.env.REACT_APP_LOCAL_STORAGE_API || '';
const OFFLINE_MODE_ENABLED = (process.env.REACT_APP_OFFLINE_MODE || '').toLowerCase() === 'true';
const LOCAL_STORAGE_TIMEOUT_MS = 2500;
const CONNECTIVITY_HTTP_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);

export interface CachedEnvelope<T> {
    updatedAt: string;
    value: T;
}

const getStorageUrl = (key: string) => {
    return `${LOCAL_STORAGE_API}/${encodeURIComponent(key)}`;
};

const safeJsonParse = <T>(raw: string | null) => {
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as T;
    } catch (error) {
        return null;
    }
};

export const isOfflineServiceEnabled = () => OFFLINE_MODE_ENABLED;

const isStorageApiAvailable = () => Boolean(
    OFFLINE_MODE_ENABLED &&
    LOCAL_STORAGE_API &&
    typeof fetch !== 'undefined'
);

const createTimeoutSignal = () => {
    if (typeof AbortController === 'undefined') {
        return null;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        controller.abort();
    }, LOCAL_STORAGE_TIMEOUT_MS);

    return {
        signal: controller.signal,
        clear: () => clearTimeout(timeoutId),
    };
};

const mirrorToStorageApi = async <T>(key: string, envelope: CachedEnvelope<T>) => {
    if (!isStorageApiAvailable()) {
        return;
    }

    const timeout = createTimeoutSignal();

    try {
        const response = await fetch(getStorageUrl(key), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(envelope),
            signal: timeout?.signal,
        });

        if (!response.ok && response.status !== 204) {
            throw new Error(`Storage PUT failed with status ${response.status}`);
        }
    } catch (error) {
    } finally {
        timeout?.clear();
    }
};

const readFromStorageApi = async <T>(key: string) => {
    if (!isStorageApiAvailable()) {
        return null;
    }

    const timeout = createTimeoutSignal();

    try {
        const response = await fetch(getStorageUrl(key), {
            method: 'GET',
            headers: {
                Accept: 'application/octet-stream, application/json, text/plain',
            },
            signal: timeout?.signal,
        });

        if (response.status === 404) {
            return null;
        }

        if (!response.ok) {
            return null;
        }

        const raw = await response.text();
        const payload = safeJsonParse<CachedEnvelope<T>>(raw);

        if (!payload || typeof payload !== 'object' || !('value' in payload)) {
            return null;
        }

        return payload;
    } catch (error) {
        return null;
    } finally {
        timeout?.clear();
    }
};

const removeFromStorageApi = async (key: string) => {
    if (!isStorageApiAvailable()) {
        return;
    }

    const timeout = createTimeoutSignal();

    try {
        await fetch(getStorageUrl(key), {
            method: 'DELETE',
            signal: timeout?.signal,
        });
    } catch (error) {
    } finally {
        timeout?.clear();
    }
};

export const buildCacheKey = (...parts: Array<string | number | null | undefined>) => {
    return parts
        .filter((part) => part !== null && part !== undefined && part !== '')
        .map((part) => String(part).replace(/[^a-zA-Z0-9._-]/g, '_'))
        .join('.');
};

export const hashPayload = (payload: unknown) => {
    const input = typeof payload === 'string' ? payload : JSON.stringify(payload ?? {});
    let hash = 5381;

    for (let index = 0; index < input.length; index++) {
        hash = (hash * 33) ^ input.charCodeAt(index);
    }

    return Math.abs(hash >>> 0).toString(36);
};

export const getCachedValue = <T>(_key: string) => {
    return null as CachedEnvelope<T> | null;
};

export const getCachedValueAsync = async <T>(key: string) => {
    return readFromStorageApi<T>(key);
};

export const cacheValue = async <T>(key: string, value: T) => {
    const envelope: CachedEnvelope<T> = {
        updatedAt: new Date().toISOString(),
        value,
    };

    await mirrorToStorageApi(key, envelope);
    return envelope;
};

export const removeCachedValue = async (key: string) => {
    await removeFromStorageApi(key);
};

export const isConnectivityError = (error: any) => {
    if (axios.isCancel(error)) {
        return false;
    }

    const status = Number(error?.response?.status);

    return (
        !error?.response ||
        error?.code === 'ERR_NETWORK' ||
        error?.code === 'ECONNABORTED' ||
        error?.name === 'AbortError' ||
        CONNECTIVITY_HTTP_STATUSES.has(status) ||
        (typeof navigator !== 'undefined' && navigator.onLine === false)
    );
};
